/* ══════════════════════════════════════════════════════════════════════════════════════════
   sw_events.js — Sweetum storefront click beacon.  STAGED 2026-10-26, NOT LIVE.

   Jorge, 2026-10-26: "something built that would track traffic on our page and clicks the
   important clicks, now that we've put all of the buttons there for the product pages."

   Shopify already reports that a session added to cart. It does NOT report which control was
   pressed or whether the photo-upload field — the configurator's hard gate — was ever opened.
   This file is the only way to see those, and it is the reason it must be boringly safe:
   IT RUNS ON EVERY PAGE FOR EVERY REAL CUSTOMER.

   ── THE SAFETY RULES, each one load-bearing ──────────────────────────────────────────────
   1. IT CAN NEVER BREAK THE STORE. Everything is inside try/catch, there are no dependencies,
      nothing is awaited, and no handler calls preventDefault or stopPropagation. If this file
      throws on line one, the page behaves exactly as it does today.
   2. IT LISTENS, IT NEVER INTERCEPTS. All listeners are passive and attached in the CAPTURE
      phase on `document`, so they see events from components that render later without the
      script needing to know when they appear. A listener that mutated or cancelled an event
      could stop someone buying, which is the one outcome worse than having no data.
   3. IT SENDS NO PERSONAL DATA. Not the uploaded photo, not the filename, not the pet's name,
      not an email, not an address. Only: an event name, the page path, the product handle, a
      coarse detail (e.g. which thumbnail index), and a per-VISIT random id. See PAYLOAD below.
   4. IT HONOURS Do Not Track and Global Privacy Control. If either is set, it sends nothing.
   5. IT FAILS SILENT. A dead endpoint, a blocked request or a full queue degrades to no data,
      never to a console error storm on a customer's screen.
   ══════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  /* The installer rewrites this line; it is the ONLY configuration. */
  var ENDPOINT = "https://sweetum-events.asksfirst.workers.dev/e";

  try {
    /* ── 0. Refuse to run in the conditions where running would be wrong. ── */
    if (!ENDPOINT || ENDPOINT.indexOf("__SW_EVENTS") === 0) return;   // not configured
    if (typeof document === "undefined" || !document.addEventListener) return;
    var nav = window.navigator || {};
    /* Do Not Track / Global Privacy Control. "1" and "yes" are both seen in the wild. */
    var dnt = String(nav.doNotTrack || window.doNotTrack || nav.msDoNotTrack || "");
    if (dnt === "1" || dnt.toLowerCase() === "yes" || nav.globalPrivacyControl === true) return;

    /* ── 1. A per-VISIT id. Random, not derived from anything about the person, and gone when
           the tab closes. It exists only so "opened the upload then left" can be told apart
           from "two different people". sessionStorage can throw in private mode. ── */
    var sid = "";
    try {
      sid = sessionStorage.getItem("sw_sid") || "";
      if (!sid) {
        sid = (Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 18);
        sessionStorage.setItem("sw_sid", sid);
      }
    } catch (e) { sid = "nostore"; }

    /* ── 2. The product handle, read from the URL. No DOM scraping, no product title. ── */
    var path = (location && location.pathname) || "";
    var m = path.match(/\/products\/([^/?#]+)/);
    var handle = m ? m[1].slice(0, 80) : "";

    /* ── 3. Transport. sendBeacon survives the page unloading, which matters because the most
           interesting event — leaving after opening the upload — happens AT unload. fetch with
           keepalive is the fallback. Neither is awaited and both are wrapped. ── */
    var sent = 0;
    var MAX_PER_PAGE = 40;          // a cap, so a loop in our own code cannot flood the endpoint
    function send(event, detail) {
      try {
        if (sent >= MAX_PER_PAGE) return;
        sent++;
        var body = JSON.stringify({
          v: 1,
          e: String(event).slice(0, 40),
          p: path.slice(0, 120),
          h: handle,
          d: detail === undefined || detail === null ? "" : String(detail).slice(0, 60),
          s: sid,
          t: Date.now()
        });
        if (nav.sendBeacon) {
          /* text/plain keeps this a CORS "simple request": no preflight, so a slow OPTIONS
             round-trip can never delay anything the customer is doing. */
          var blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
          if (nav.sendBeacon(ENDPOINT, blob)) return;
        }
        if (window.fetch) {
          fetch(ENDPOINT, {
            method: "POST", body: body, keepalive: true, mode: "cors",
            headers: { "Content-Type": "text/plain;charset=UTF-8" }
          })["catch"](function () { /* a dead endpoint is not the customer's problem */ });
        }
      } catch (e) { /* never surface */ }
    }

    /* ── 4. A tiny helper: does this element (or an ancestor) match? Used so a click on the
           <svg> inside a button still credits the button. ── */
    function closest(el, sel) {
      try {
        while (el && el.nodeType === 1) {
          if (el.matches && el.matches(sel)) return el;
          el = el.parentElement;
        }
      } catch (e) { /* fall through */ }
      return null;
    }

    /* ── 5. THE SIX EVENTS, plus a page view for the denominator.
           Selectors are deliberately BROAD (a file input is a file input) rather than pinned to
           one product's ids, because #sw-k-ornament-photo exists only on the ornament and the
           same flow must be measurable on the mug, case and canvas. ── */
    send("page_view", document.referrer ? "ref" : "direct");

    /* 5a. PHOTO UPLOAD — the configurator's hard gate and the likeliest drop-off.
           THREE distinct facts, which is the whole point: the field was OPENED, a file was
           actually CHOSEN, or it was opened and never chosen. */
    document.addEventListener("click", function (ev) {
      try {
        var t = ev.target;
        var fileCtl = closest(t, 'input[type="file"]');
        if (!fileCtl) {
          /* A label pointing at a file input opens the picker too. */
          var lab = closest(t, "label[for]");
          if (lab) {
            var tgt = document.getElementById(lab.getAttribute("for"));
            if (tgt && tgt.type === "file") fileCtl = tgt;
          }
        }
        if (fileCtl) { send("photo_upload_opened"); return; }

        /* 5b. GALLERY THUMBNAIL — which images actually get looked at. */
        var thumb = closest(t, "button.sw-k-thumb, [data-gallery]");
        if (thumb) {
          send("gallery_thumb", thumb.getAttribute("data-gallery") || "");
          return;
        }

        /* 5e. CROSS-SELL — the in-body links between products. Header/footer nav excluded, so
               this measures the product page's own merchandising, not the chrome. */
        var a = closest(t, "a[href]");
        if (a && !closest(a, "header, footer, nav, .menu-drawer, .cart-drawer")) {
          var href = a.getAttribute("href") || "";
          if (/^\/(products|collections|pages)\//.test(href)) send("crosssell_click", href.slice(0, 60));
        }
      } catch (e) { /* never surface */ }
    }, true);

    /* 5a-ii. A FILE WAS REALLY CHOSEN. The filename is deliberately NOT sent — only that a
             file exists and roughly how big, which is enough to spot a failed upload. */
    document.addEventListener("change", function (ev) {
      try {
        var t = ev.target;
        if (!t) return;
        if (t.type === "file") {
          var n = (t.files && t.files.length) || 0;
          var kb = (n && t.files[0] && t.files[0].size) ? Math.round(t.files[0].size / 1024) : 0;
          send(n > 0 ? "photo_selected" : "photo_cleared", n > 0 ? kb + "kb" : "");
          return;
        }
        /* 5c. PACK / VARIANT SELECTION — upsell interest. The VALUE is sent (e.g. "Two
               ornaments"), which is product data, not personal data. */
        if (t.tagName === "SELECT") {
          var opt = t.options && t.options[t.selectedIndex];
          send("variant_selected", (opt && opt.text ? opt.text : t.value || "").slice(0, 60));
        }
      } catch (e) { /* never surface */ }
    }, true);

    /* 5d. "PRODUCT DETAILS" EXPANDED — a real interest signal. `toggle` does not bubble, so it
           is captured at the document level where capture-phase listeners still see it. */
    document.addEventListener("toggle", function (ev) {
      try {
        var d = ev.target;
        if (d && d.tagName === "DETAILS" && d.open) {
          var sum = d.querySelector("summary");
          send("details_expanded", (sum && sum.textContent ? sum.textContent.trim() : "").slice(0, 40));
        }
      } catch (e) { /* never surface */ }
    }, true);

    /* 5f. ADD TO CART — Shopify also counts this, and that is deliberate: it is the ONE event
           both systems see, so it is the cross-check that tells us whether this beacon is
           under-reporting (ad blockers, no-JS) rather than leaving us guessing. */
    document.addEventListener("submit", function (ev) {
      try {
        var f = ev.target;
        if (f && f.tagName === "FORM" && /\/cart\/add/.test(f.getAttribute("action") || "")) {
          send("add_to_cart", handle);
        }
      } catch (e) { /* never surface */ }
    }, true);

    /* ── 6. THE DROP-OFF FACT, computed at unload: the upload was opened and no file was ever
           chosen. This is the single most valuable thing the beacon can tell us and it cannot
           be derived server-side. visibilitychange is used because `unload` is unreliable on
           mobile Safari. ── */
    var openedUpload = false, choseFile = false, reported = false;
    document.addEventListener("click", function (ev) {
      try { if (closest(ev.target, 'input[type="file"], label[for]')) openedUpload = true; } catch (e) {}
    }, true);
    document.addEventListener("change", function (ev) {
      try { if (ev.target && ev.target.type === "file" && ev.target.files && ev.target.files.length) choseFile = true; } catch (e) {}
    }, true);
    document.addEventListener("visibilitychange", function () {
      try {
        if (document.visibilityState !== "hidden" || reported) return;
        if (openedUpload && !choseFile) { reported = true; send("photo_abandoned"); }
      } catch (e) { /* never surface */ }
    }, true);

  } catch (e) {
    /* The outermost net. If anything above threw, the storefront is unaffected. */
  }
})();
